// CategoryBanner Component - Main categories showcase section for the homepage
// This component displays the main categories in an interactive carousel format with
// search functionality, animated elements, and responsive design. It serves as the
// primary way for users to discover and navigate to different recipe categories.
//
// USAGE LOCATIONS:
// - src/pages/HomePage.jsx (main homepage categories section)
//
// FEATURES:
// - Dynamic category loading from Supabase with fallback to static data
// - Interactive search functionality with suggestions
// - Animated search icon with pulsing effects
// - Responsive carousel using CategoryCarousel component
// - Wave dividers for visual appeal
// - Intersection Observer for scroll-triggered animations
// - Search tutorial tooltip for first-time users
// - Smooth transitions between search and title states
//
// DATA SOURCES:
// - Primary: Supabase 'categories' table
// - Fallback: Static kuchniaCategories from category-data.js
//
// SEARCH INTEGRATION:
// - Uses SearchBar component for search functionality
// - Integrates with searchService for suggestions
// - Navigates to search results page

import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import CategoryCarousel from '../UI/CategoryCarousel';
import { kuchniaCategories } from '../../Data/category-data'; 
import { useInView } from 'react-intersection-observer';
import SearchBar from '../UI/SearchBar';
import { FaSearch } from 'react-icons/fa';
import searchService from '../../services/searchService';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase-browser';
import { getImageUrl } from '../../utils/imageUtils';

// Constants
const BG_COLOR_LIGHTER = "gray-100";
const SECTION_BG = `bg-${BG_COLOR_LIGHTER}`;
const ACCENT_COLOR = 'bg-green-600'; // Solid green for consistency with buttons

// Animation variants
const animations = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1.0], 
      staggerChildren: 0.1
    }
  }
};

const itemAnimation = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1.0] }
  }
};

const titleVariant = {
  hidden: { y: 10, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { 
      type: "spring",
      stiffness: 400,
      damping: 30
    }
  }
};

// Title with search icon component (memoized)
const TitleWithSearch = memo(({ title, toggleSearch, accentColor, showTooltip }) => {
  return (
    <div className="flex items-center justify-center flex-wrap gap-3 sm:gap-4">
      <motion.div
        className="relative group cursor-pointer"
        onClick={toggleSearch}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        title="Kliknij, aby wyszukać przepisy"
      >
        {/* Subtle background effect on hover */}
        <motion.div 
          className="absolute inset-0 -m-2 rounded-lg bg-green-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        />
        
        <h2 
          className={`relative font-['Playfair_Display'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-[#1A202C] 
            pb-3 sm:pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 
            after:-translate-x-1/2 after:w-24 sm:after:w-28 md:after:w-32 after:h-[3px] after:${accentColor} 
            tracking-wide font-semibold max-w-[80%] sm:max-w-none break-words
            group-hover:text-green-600 transition-colors duration-300`}
        >
          {title}
        </h2>
      </motion.div>
      
      <div className="flex-shrink-0">
        <SearchIcon toggleSearch={toggleSearch} showTooltip={showTooltip} />
      </div>
    </div>
  );
});

TitleWithSearch.displayName = 'TitleWithSearch';

// Search icon component (memoized)
const SearchIcon = memo(({ toggleSearch, showTooltip }) => {
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      const hasSeenTutorial = localStorage.getItem('hasSeenSearchTutorial');
      return hasSeenTutorial !== 'true';
    } catch (e) {
      return true;
    }
  });

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640;
    }
    return false;
  });

  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => {
        setShowTutorial(false);
        try {
          localStorage.setItem('hasSeenSearchTutorial', 'true');
        } catch (e) {}
      }, 16000);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative inline-block">
      <motion.div 
        className="cursor-pointer relative select-none search-icon-container group flex items-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleSearch}
        initial={{ scale: 1 }}
        style={{ left: '0', ...(isMobile ? { left: '-15px' } : {}) }}
      >
        <div className="relative flex items-center justify-center">
          <FaSearch 
            className="text-[2.86rem] sm:text-[2.85rem] md:text-[3.1rem] text-green-600 hover:text-green-500 transition-colors duration-300 drop-shadow-lg relative z-10 search-icon" 
            style={isMobile ? { fontSize: '2.86rem' } : {}}
          />
        </div>
      </motion.div>
    </div>
  );
});

SearchIcon.displayName = 'SearchIcon';

// Wave divider component (memoized)
const WaveDivider = memo(({ position = 'top', color }) => {
  const isTop = position === 'top';
  const opacity = isTop ? '1' : '0.6'; // Make bottom divider more subtle
  return (
    <div className={`absolute ${isTop ? 'top-0' : 'bottom-0'} left-0 right-0 w-full overflow-hidden ${!isTop ? 'transform rotate-180' : ''}`}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[70px] sm:h-[120px]">
        <path 
          d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
          fill={color}
          opacity={opacity}
        ></path>
      </svg>
    </div>
  );
});

WaveDivider.displayName = 'WaveDivider';

// Default image when category has no image in Supabase (so we never drop categories)
const DEFAULT_CATEGORY_IMAGE = 'ciasta.jpg';

// Map raw categories from Supabase (or prefetch) to carousel items; supports full image URLs and local paths.
// All categories from Supabase are shown – missing image uses default (no filtering out).
function mapCategoriesToItems(categories) {
  if (!categories?.length) return [];
  return categories.map((category) => {
    let imagePath = category.image_url || category.image_path || category.image;
    if (!imagePath) {
      imagePath = getImageUrl(DEFAULT_CATEGORY_IMAGE);
    } else if (imagePath.startsWith('http') || imagePath.startsWith('/')) {
      // Full URL (Supabase storage or external) – use as is
    } else {
      if (imagePath === 'categories/ciasta.webp') imagePath = 'ciasta.jpg';
      else if (imagePath.includes('categories/') && imagePath.includes('.webp')) {
        const name = imagePath.replace('categories/', '').replace('.webp', '');
        imagePath = `${name}.jpg`;
      }
      imagePath = getImageUrl(imagePath);
    }
    return {
      id: category.id,
      label: category.name || category.label,
      image: imagePath,
      shortDesc: category.description || category.shortDesc || 'Odkryj nasze pyszne przepisy!',
      link: category.link || `/kuchnia/${category.slug || category.id}`,
    };
  });
}

// Main component
const CategoryBanner = ({ prefetchedCategories = [] }) => {
  // State (used when no prefetched data)
  const [allCategoryItems, setAllCategoryItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const navigate = useNavigate();
  
  // Refs
  const bannerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Handlers
  const toggleSearch = useCallback(() => {
    setIsSearching(prev => !prev);
    // Reset suggestions and search term when toggling search
    setSuggestions([]);
    setSearchTerm('');
  }, []);
  
  const handleSearchClose = useCallback(() => {
    setIsSearching(false);
    setSuggestions([]);
    setSearchTerm('');
  }, []);

  // Search function with debounce
  const performSearch = useCallback(async (term) => {
    if (term.length < 3) {
      setSuggestions([]);
      return;
    }
    
    try {
      setSearchTerm(term);
      
      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set a new timeout to debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        // Get search suggestions from service
        const formattedSuggestions = await searchService.getSuggestions(term);
        setSuggestions(formattedSuggestions);
      }, 300); // 300ms debounce delay
    } catch (error) {
      console.error("Error performing search:", error);
      setSuggestions([]);
    }
  }, []);

  // Handle search input changes
  const handleSearchInput = useCallback((term) => {
    performSearch(term);
  }, [performSearch]);

  // Handle selecting a suggestion
  const handleSuggestionSelect = useCallback((suggestion) => {
    if (suggestion && suggestion.name) {
      // Navigate to search page with the suggestion name as query
      navigate(`/search?q=${encodeURIComponent(suggestion.name)}`);
      setIsSearching(false);
      setSuggestions([]);
      setSearchTerm('');
    }
  }, [navigate]);

  // Handle search submission
  const handleSearchSubmit = useCallback((term) => {
    if (term.trim()) {
      navigate(`/search?q=${encodeURIComponent(term)}`);
      setIsSearching(false);
      setSuggestions([]);
      setSearchTerm('');
    }
  }, [navigate]);

  // Derived: use prefetched categories from HomePage when available, otherwise internal fetch result
  const categoryItems = prefetchedCategories?.length
    ? mapCategoriesToItems(prefetchedCategories)
    : allCategoryItems;
  const categoriesLoaded = prefetchedCategories?.length > 0 || isLoaded;

  // Load categories (only when no prefetched data – e.g. initial load before parent fetch completes)
  useEffect(() => {
    if (prefetchedCategories?.length) return;

    const fetchCategories = async () => {
      try {
        const { data: categories, error } = await supabase
          .from('categories')
          .select('*')
          .order('id', { ascending: true });

        if (error) {
          console.error("Error fetching categories:", error);
          loadFallbackCategories();
          return;
        }

        const items = mapCategoriesToItems(categories || []);
        if (items.length === 0) {
          loadFallbackCategories();
        } else {
          setAllCategoryItems(items);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        loadFallbackCategories();
      }
    };

    // Tylko gdy Supabase zwróci błąd lub 0 kategorii – wtedy pokazujemy zahardkodowaną listę z category-data.js
    const loadFallbackCategories = () => {
      const items = kuchniaCategories.mainCategories
        .filter((c) => c.image)
        .map((c) => ({
          id: c.label,
          label: c.label,
          image: getImageUrl(c.image),
          shortDesc: c.shortDesc || 'Odkryj nasze pyszne przepisy!',
          link: c.link,
        }));
      setAllCategoryItems(items);
      setIsLoaded(true);
    };

    fetchCategories();
  }, [prefetchedCategories?.length]);

  // Start animations when in view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Escape key handler - only when search is open
  // SearchBar also handles Escape but only for suggestions, this handles closing the search
  useEffect(() => {
    if (!isSearching) return;
    
    const handleEscKey = (event) => {
      // Only handle if suggestions are not showing (let SearchBar handle Escape for suggestions)
      if (event.key === 'Escape' && suggestions.length === 0) {
        handleSearchClose();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isSearching, suggestions.length, handleSearchClose]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Define wave divider color
  const dividerColor = '#F7FAFC'; // tailwind bg-gray-50

  return (
    <section 
      id="categories" 
      ref={bannerRef} 
      className={`relative ${SECTION_BG} min-h-[320px] sm:min-h-[350px] md:min-h-[420px] overflow-hidden py-24`}
    >
      {/* Wavy dividers */}
      <WaveDivider position="top" color={dividerColor} />

      <motion.div 
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={animations}
        className="container mx-auto px-4 pt-10 sm:pt-12 md:pt-16 lg:pt-20"
      >
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8 md:mb-12 relative"
          variants={itemAnimation}
        >
          <div className="flex flex-col items-center justify-center">
            {/* Title and SearchBar with smoother transitions */}
            <AnimatePresence mode="wait" initial={false}>
              {!isSearching ? (
                <motion.div
                  key="title-container"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 1,
                    ease: [0.25, 0.1, 0.25, 1.0] // Smooth cubic bezier curve
                  }}
                >
                  <TitleWithSearch 
                    title="ODŻYWCZE PRZEPISY" 
                    toggleSearch={toggleSearch} 
                    accentColor={ACCENT_COLOR}
                    showTooltip={!isSearching}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="searchbar-container"
                  initial={{ opacity: 0, y: 20, width: "80%" }}
                  animate={{ opacity: 1, y: 0, width: "100%" }}
                  exit={{ opacity: 0, y: 20, width: "80%" }}
                  transition={{ 
                    duration: 1,
                    ease: [0.25, 0.1, 0.25, 1.0],
                    width: { duration: 0.8 }
                  }}
                  className="w-full mx-auto py-2"
                  style={{ maxWidth: "calc(100% - 40px)" }}
                >
                  <SearchBar 
                    placeholder="Szukaj przepisów..." 
                    onSearchSubmit={handleSearchSubmit}
                    onClose={handleSearchClose}
                    onChange={handleSearchInput}
                    initialOpen={true}
                    suggestions={suggestions}
                    onSuggestionSelect={handleSuggestionSelect}
                    highlightedTerm={searchTerm}
                    minCharsForSuggestions={3}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          
            {/* Description - always visible */}
            <motion.p 
              className="mt-2 sm:mt-3 md:mt-4 text-gray-600 text-base sm:text-lg md:text-xl lg:text-2xl max-w-2xl sm:max-w-3xl mx-auto font-['Lato'] leading-relaxed tracking-wide"
              variants={titleVariant}
            >
              Odkryj nasze starannie wybrane przepisy, które łączą w sobie smak i wartości odżywcze
            </motion.p>
          </div>
        </motion.div>
        
        {/* Carousel Section */}
        <motion.div 
          variants={itemAnimation}
          className="w-full mx-auto"
        >
          {categoriesLoaded ? (
            categoryItems.length > 0 ? (
              <div className="relative flex justify-center">
                <motion.div 
                  className={`absolute inset-0 bg-gradient-to-r from-transparent via-${BG_COLOR_LIGHTER} to-transparent 
                    opacity-0 pointer-events-none`} 
                  animate={{
                    opacity: [0, 0.4, 0],
                    transition: { 
                      duration: 5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }
                  }}
                />
                
                <div className="relative px-1 sm:px-2 md:px-4 lg:px-8 overflow-hidden w-full max-w-[100%] md:max-w-[900px] lg:max-w-[1200px] xl:max-w-[1400px] mx-auto">
                  <CategoryCarousel 
                    items={categoryItems}
                    showViewButton={true}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 text-lg">Nie znaleziono kategorii.</p>
              </div>
            )
          ) : (
            <div className="flex justify-center items-center py-20 min-h-[280px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Bottom wavy divider */}
      <WaveDivider position="bottom" color="white" />
    </section>
  );
};

export default memo(CategoryBanner);